<template>
  <div>
    <v-form
      v-if="property"
      ref="form"
      :data="form"
      :url="
        $laravel.route('api.property.statuses.store_property_status', {
          property: property.id,
        })
      "
      :method="'post'"
      @success="success"
    >
      <div class="field">
        <label class="label">{{ $t('status').capitalize() }}</label>
        <!-- Explanatory radio cards instead of a bare dropdown: each status
             carries its description so the right one gets picked -->
        <div class="status-choice-list">
          <label
            v-for="(status, index) in statuses"
            :key="index"
            class="status-choice"
            :class="{ 'is-selected': form.type === status.key }"
          >
            <input
              type="radio"
              name="status-type"
              class="status-choice-radio"
              :value="status.key"
              :checked="form.type === status.key"
              @change="onTypePicked(status.key)"
            >
            <span class="status-choice-body">
              <span class="status-choice-title">{{ status.label.capitalize() }}</span>
              <span
                v-if="statusDescription(status.key)"
                class="status-choice-desc"
                v-html="statusDescription(status.key)"
              />
            </span>
          </label>
        </div>
      </div>
      <div v-if="statusSchema && statusSchema.schema && !loading">
        <div
          v-for="(field, index) in statusSchema.schema.fields"
          :key="index"
          class="field"
        >
          <v-input-date
            v-if="isFieldVisible('vInputDate', field)"
            v-model="form[field.name]"
            :value="field.attributes.value"
            :label="field.label.capitalize()"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled || isAvailabilityLocked(field)"
            :name="field.name"
          />
          <v-input
            v-if="isFieldVisible('vInputEmail', field)"
            v-model="form[field.name]"
            :label="field.label.capitalize()"
            :placeholder="placeholder(field)"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled"
            :name="field.name"
            :has-info="field.attributes.has_info"
            :class="{ 'custom-tooltip-position': field.attributes.has_info }"
            info-size="is-large"
            info-type="html"
            type="email"
          >
            <template
              v-if="field.attributes.has_info"
              #info
            >
              <p class="has-text-left">
                {{ field.attributes.text_info }}
              </p>
            </template>
          </v-input>

          <v-input
            v-if="isFieldVisible('vInputText', field)"
            v-model="form[field.name]"
            :label="field.label.capitalize()"
            :placeholder="placeholder(field)"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled"
            :name="field.name"
            :has-info="field.attributes.has_info"
            :class="{ 'custom-tooltip-position': field.attributes.has_info }"
            info-size="is-large"
            info-type="html"
            type="text"
          >
            <template
              v-if="field.attributes.has_info"
              #info
            >
              <p class="has-text-left">
                {{ field.attributes.text_info }}
              </p>
            </template>
          </v-input>
          <v-input
            v-if="isFieldVisible('vInputTextarea', field)"
            v-model="form[field.name]"
            :label="field.label.capitalize()"
            :placeholder="placeholder(field)"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled"
            :name="field.name"
            type="textarea"
            :rows="4"
          />
          <v-input-switch
            v-if="isFieldVisible('vInputSwitch', field)"
            v-model="form[field.name]"
            :required="isFieldRequired(field)"
            :true-value="true"
            :label="field.label.capitalize()"
            :false-value="false"
            label-position="right"
            :disabled="field.attributes.disabled"
            :name="field.name"
          />

          <v-input
            v-if="isFieldVisible('vInputNumber', field)"
            v-model="form[field.name]"
            :label="field.label.capitalize()"
            :placeholder="placeholder(field)"
            type="number"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled"
            :name="field.name"
            :has-info="field.attributes.has_info"
            :class="{ 'custom-tooltip-position': field.attributes.has_info }"
            info-size="is-large"
            info-type="html"
          >
            <template
              v-if="field.attributes.has_info"
              #info
            >
              <p class="has-text-left">
                {{ field.attributes.text_info }}
              </p>
            </template>
          </v-input>

          <v-input-rich-text
            v-if="isFieldVisible('vInputRichText', field)"
            v-model="form[field.name]"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled"
            :label="field.label.capitalize()"
            :name="field.name"
            :has-info="field.attributes.has_info"
            :class="{ 'custom-tooltip-position': field.attributes.has_info }"
            info-size="is-large"
            info-type="html"
          >
            <template
              v-if="field.attributes.has_info"
              #info
            >
              <p class="has-text-center">
                {{ field.attributes.text_info }}
              </p>
            </template>
          </v-input-rich-text>

          <v-input-select
            v-if="isFieldVisible('vInputSelect', field)"
            v-model="form[field.name]"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled"
            :label="field.label.capitalize()"
            :name="field.name"
          >
            <option
              v-for="option in field.attributes.options"
              :key="option.key"
              :value="option.key"
            >
              {{ option.label.capitalize() }}
            </option>
          </v-input-select>

          <v-input-tag
            v-if="isFieldVisible('vInputTag', field)"
            v-model="form[field.name]"
            :required="isFieldRequired(field)"
            :disabled="field.attributes.disabled"
            :label="field.label.capitalize()"
            :name="field.name"
            field="label"
            :data="filterTagsData(field.attributes.options, field.name)"
            :open-on-focus="true"
          />
          <v-input
            v-if="isFieldVisible('vInputRelation', field)"
            v-model="form[field.name]"
            :label="field.label.capitalize()"
            :name="field.name"
            type="text"
          />

          <!-- "À louer" readiness module, right under vacant/occupé (not
               asked when the unit simply comes back from an application:
               everything stays as it was) -->
          <template v-if="field.name === 'is_occupied' && form.type === 'for_rent' && !backFromApplication">
            <!-- VACANT: is the unit 100% photo-ready? -->
            <div
              v-if="form.is_occupied === false"
              class="ready-module"
            >
              <p class="label mb-1 has-text-weight-normal">
                <span v-html="$t('photo_ready_question')" />
                <span class="has-text-danger">*</span>
              </p>
              <p class="ready-hint">
                {{ $t("photo_ready_hint") }}
              </p>
              <div class="status-choice-list">
                <label
                  class="status-choice"
                  :class="{ 'is-selected': form.photo_ready === true }"
                >
                  <input
                    type="radio"
                    name="photo-ready"
                    class="status-choice-radio"
                    :checked="form.photo_ready === true"
                    @change="setPhotoReady(true)"
                  >
                  <span class="status-choice-body">
                    <span class="status-choice-title">{{ $t("photo_ready_yes") }}</span>
                    <span class="status-choice-desc">{{ $t("photo_ready_yes_hint") }}</span>
                  </span>
                </label>
                <label
                  class="status-choice"
                  :class="{ 'is-selected': form.photo_ready === false }"
                >
                  <input
                    type="radio"
                    name="photo-ready"
                    class="status-choice-radio"
                    :checked="form.photo_ready === false"
                    @change="setPhotoReady(false)"
                  >
                  <span class="status-choice-body">
                    <span class="status-choice-title">{{ $t("photo_ready_no") }}</span>
                    <span class="status-choice-desc">{{ $t("photo_ready_no_hint") }}</span>
                  </span>
                </label>
              </div>
              <template v-if="form.photo_ready === false">
                <b-field
                  :label="$t('photo_ready_remaining_label')"
                  class="mt-3"
                >
                  <b-input
                    v-model="form.photo_ready_remaining"
                    type="textarea"
                    rows="3"
                  />
                </b-field>
              </template>
            </div>

            <!-- OCCUPIED: inspection in the last 30 days? -->
            <div
              v-if="form.is_occupied === true"
              class="ready-module"
            >
              <p class="label mb-1 has-text-weight-normal">
                <span v-html="$t('recent_inspection_question')" />
                <span class="has-text-danger">*</span>
              </p>
              <div class="status-choice-list">
                <label
                  class="status-choice"
                  :class="{ 'is-selected': form.recent_inspection === true }"
                >
                  <input
                    type="radio"
                    name="recent-inspection"
                    class="status-choice-radio"
                    :checked="form.recent_inspection === true"
                    @change="form.recent_inspection = true"
                  >
                  <span class="status-choice-body">
                    <span
                      class="status-choice-title is-sentence"
                      v-html="$t('recent_inspection_yes')"
                    />
                  </span>
                </label>
                <label
                  class="status-choice"
                  :class="{ 'is-selected': form.recent_inspection === false }"
                >
                  <input
                    type="radio"
                    name="recent-inspection"
                    class="status-choice-radio"
                    :checked="form.recent_inspection === false"
                    @change="form.recent_inspection = false"
                  >
                  <span class="status-choice-body">
                    <span
                      class="status-choice-title is-sentence"
                      v-html="$t('recent_inspection_no')"
                    />
                  </span>
                </label>
              </div>
              <template v-if="form.recent_inspection === true">
                <!-- Reuse an existing Travaux et inspection record, or
                     create a new one from this attestation -->
                <div class="status-choice-list mt-3">
                  <label
                    class="status-choice"
                    :class="{ 'is-selected': form.inspection_mode === 'new' }"
                  >
                    <input
                      type="radio"
                      name="inspection-mode"
                      class="status-choice-radio"
                      :checked="form.inspection_mode === 'new'"
                      @change="form.inspection_mode = 'new'"
                    >
                    <span class="status-choice-body">
                      <span class="status-choice-title">{{ $t("inspection_mode_new") }}</span>
                    </span>
                  </label>
                  <label
                    v-if="existingInspections.length"
                    class="status-choice"
                    :class="{ 'is-selected': form.inspection_mode === 'existing' }"
                  >
                    <input
                      type="radio"
                      name="inspection-mode"
                      class="status-choice-radio"
                      :checked="form.inspection_mode === 'existing'"
                      @change="form.inspection_mode = 'existing'"
                    >
                    <span class="status-choice-body">
                      <span class="status-choice-title">{{ $t("inspection_mode_existing") }}</span>
                    </span>
                  </label>
                </div>

                <b-field
                  v-if="form.inspection_mode === 'existing'"
                  :label="$t('inspection_existing_label')"
                  class="mt-3"
                >
                  <b-select
                    v-model="form.existing_inspection_id"
                    expanded
                  >
                    <option
                      v-for="insp in existingInspections"
                      :key="insp.id"
                      :value="insp.id"
                    >
                      {{ insp.date }} — {{ (insp.elements || []).length }} {{ $t("inspection_elements_short") }}
                    </option>
                  </b-select>
                </b-field>

                <template v-if="form.inspection_mode === 'new'">
                  <b-field
                    :label="$t('inspection_description_label')"
                    class="mt-3"
                  >
                    <b-input
                      v-model="form.inspection_description"
                      type="textarea"
                      rows="3"
                    />
                  </b-field>
                  <b-field :label="$t('inspection_photos_label')">
                    <b-upload
                      v-model="inspectionPhotoFiles"
                      multiple
                      drag-drop
                      expanded
                      accept="image/*"
                      @input="onInspectionPhotos"
                    >
                      <section class="section py-4">
                        <div class="content has-text-centered">
                          <b-icon
                            icon="camera"
                            size="is-medium"
                          />
                          <p class="is-size-7 mb-0">
                            {{ $t("drop_or_click_to_upload") }}
                          </p>
                        </div>
                      </section>
                    </b-upload>
                  </b-field>
                  <div
                    v-if="(form.inspection_photos || []).length"
                    class="inspection-thumbs"
                  >
                    <img
                      v-for="(photo, photoIndex) in form.inspection_photos"
                      :key="photoIndex"
                      :src="photo"
                      class="inspection-thumb"
                      :title="$t('delete')"
                      @click="removeInspectionPhoto(photoIndex)"
                    >
                  </div>
                </template>
              </template>
            </div>
          </template>
        </div>
      </div>
      <footer class="mt-5">
        <b-button
          :label="$t(closeLabel).capitalize()"
          @click="close"
        />
        <button
          class="button is-primary"
          type="submit"
          :disabled="form.type == '' || !readinessAnswered"
        >
          {{ $t("save") }}
        </button>
      </footer>
    </v-form>
  </div>
</template>

<script>
export default {
  props: {
    property: {
      required: true,
      type: Object,
    },
    firstStatus: {
      default: false,
      type: Boolean,
    },
    closeLabel: {
      default: "close",
      type: String,
    },
  },
  data() {
    return {
      statusSchema: {},
      statuses: [],
      inspectionPhotoFiles: [],
      existingInspections: [],
      form: {
        property_id: null,
        type: "",
      },
      originalForm: {
        property_id: null,
        type: "",
      },
    };
  },
  computed: {
    // Current status is "En application": going back to "À louer" keeps
    // every answer already given, nothing to re-ask
    backFromApplication() {
      return (
        this.property &&
        this.property.latest_status &&
        this.property.latest_status.type === "in_application"
      );
    },
    // "À louer" requires the readiness module to be answered before saving
    readinessAnswered() {
      if (this.form.type !== "for_rent" || this.backFromApplication) {
        return true;
      }
      if (this.form.is_occupied === true) {
        if (this.form.recent_inspection === true && this.form.inspection_mode === "existing") {
          return !!this.form.existing_inspection_id;
        }

        return this.form.recent_inspection === true || this.form.recent_inspection === false;
      }
      if (this.form.is_occupied === false) {
        if (this.form.photo_ready === true) {
          return true;
        }
        if (this.form.photo_ready === false) {
          return !!this.form.availability_date;
        }
        return false;
      }

      return false;
    },
  },
  watch: {
    form: {
      handler(newValues, oldValues) {
        this.formUpdated(newValues, oldValues);
      },
      deep: true,
    },
    // Switching vacant/occupé resets the readiness answers
    "form.is_occupied"() {
      if (this.form.type !== "for_rent" || this.backFromApplication) {
        return;
      }
      this.form.photo_ready = null;
      this.form.photo_ready_remaining = null;
      this.form.recent_inspection = null;
      this.form.inspection_description = null;
      this.form.inspection_photos = [];
      this.form.inspection_mode = "new";
      this.form.existing_inspection_id = null;
      this.inspectionPhotoFiles = [];
    },
    // Fetch the property's inspection records for the "existing" picker
    "form.recent_inspection"(value) {
      if (value === true && !this.existingInspections.length) {
        this.$http
          .get(`/api/properties/${this.property.id}/property-inspections`, {
            params: { per_page: 10 },
          })
          .then((response) => {
            this.existingInspections = response.data.data || [];
          })
          .catch(() => {});
      }
    },
  },
  created() {
    this.originalForm.property_id = this.property.id;
    let status = null;
    if (this.firstStatus) {
      status = "null_status";
    }
    this.getStatusDetails(status, true);
  },
  methods: {
    onTypePicked(statusKey) {
      this.form.type = statusKey;
      this.getStatusDetails(statusKey);
    },
    // Vacant flow: certified-ready locks the availability date to today;
    // not-ready keeps the same field editable, relabelled "100% prêt le"
    setPhotoReady(value) {
      this.form.photo_ready = value;
      if (value === true) {
        this.form.availability_date = this.$dayjs().format("YYYY-MM-DD");
      }
    },
    isAvailabilityLocked(field) {
      return (
        field.name === "availability_date" &&
        this.form.type === "for_rent" &&
        this.form.is_occupied === false &&
        this.form.photo_ready === true
      );
    },
    onInspectionPhotos(files) {
      const readers = (files || []).map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          })
      );

      Promise.all(readers).then((results) => {
        // Append: each drop/selection adds to the batch
        this.form.inspection_photos = [
          ...(this.form.inspection_photos || []),
          ...results.filter(Boolean),
        ];
        this.inspectionPhotoFiles = [];
      });
    },
    removeInspectionPhoto(index) {
      this.form.inspection_photos.splice(index, 1);
    },
    statusDescription(statusKey) {
      const key = "status_desc_" + statusKey;
      const text = this.$t(key);

      // vue-i18n returns the key itself when no translation exists
      return text === key ? "" : text;
    },
    filterTagsData(data, field) {
      if (!this.form[field]) {
        return data;
      }

      return data.filter(
        (tag) => !this.form[field].some((selected) => selected.key === tag.key)
      );
    },
    formUpdated(newValues, oldValues) {
      let fieldsVisibilities = null;
      let fieldsLabels = null;

      if (this.statusSchema.schema.fields) {
        fieldsVisibilities = this.statusSchema.schema.fields.filter(
          (field) => field.visibility_if != undefined
        );

        fieldsLabels = this.statusSchema.schema.fields.filter(
          (field) => field.labels != undefined
        );
      }

      if (fieldsLabels) {
        fieldsLabels.map((field) => {
          var index = this.statusSchema.schema.fields.findIndex(
            (f) => f.name == field.name
          );

          if (index >= 0) {
            let label = field.label;

            for (let i = 0; i < field.labels.length; i++) {
              if (newValues[field.labels[i].key] == field.labels[i].value) {
                label = field.labels[i].label;
              }
            }
            this.statusSchema.schema.fields[index].label = label;
          }
        });
      }

      if (fieldsVisibilities) {
        fieldsVisibilities.map((field) => {
          const index = this.statusSchema.schema.fields.findIndex(
            (f) => f.name === field.name
          );

          if (index >= 0) {
            if (
              typeof field.visibility_if === "object" &&
              Array.isArray(field.visibility_if) &&
              field.visibility_if.length > 0
            ) {
              const operator = field.visibility_operator || "AND"; // Default to "AND"

              let visibility =
                operator === "AND"
                  ? field.visibility_if.every(({ key, value }) => {
                    const newValue = newValues[key];
                    return Array.isArray(newValue)
                      ? newValue.some((item) => item.key === value)
                      : newValue === value;
                  })
                  : field.visibility_if.some(({ key, value }) => {
                    const newValue = newValues[key];
                    return Array.isArray(newValue)
                      ? newValue.some((item) => item.key === value)
                      : newValue === value;
                  });

              this.statusSchema.schema.fields[index].visibility = visibility;
            } else {
              if (newValues[field.visibility_if.key] === field.visibility_if.value) {
                this.statusSchema.schema.fields[index].visibility = true;
              } else {
                this.statusSchema.schema.fields[index].visibility = false;
              }
            }
          }
        });
      }
    },
    isFieldVisible(fieldType, field) {
      let isFieldVisible = field.visibility;
      return isFieldVisible && field.component == fieldType;
    },
    isFieldRequired(field) {
      if (field.required_if) {
        if (this.form[field.required_if.key] == field.required_if.value) {
          return true;
        }
      }
      return field.attributes.required;
    },
    success(event) {
      this.$emit("success", event);
    },
    close() {
      this.$emit("close", true);
    },
    getStatusDetails(statusKey, force = false) {
      this.loading = false;
      let property_id = this.property.id;
      this.$http
        .get(
          this.$laravel.route("api.property.statuses.get_status_details", {
            property: property_id,
            status: statusKey,
          })
        )
        .then((response) => {
          this.loading = false;
          this.statusSchema = response.data.data;
          if (force) {
            this.statuses = this.statusSchema.status.transitions;
          } else {
            if (this.statusSchema.schema.fields) {
              this.updateFormAttributes(
                this.statusSchema.schema.fields,
                statusKey
              );
            }
          }
          this.$emit("status", this.statusSchema.schema.key);
        });
      this.loading = false;
    },
    updateFormAttributes(fields, statusKey) {
      let newForm = {};
      newForm.property_id = this.originalForm.property_id;
      newForm.type = statusKey;
      fields.map((field) => {
        newForm[field.name] = field.attributes.value;
      });
      // Readiness module answers (reactive from the start)
      newForm.photo_ready = null;
      newForm.photo_ready_remaining = null;
      newForm.recent_inspection = null;
      newForm.inspection_description = null;
      newForm.inspection_photos = [];
      newForm.inspection_mode = "new";
      newForm.existing_inspection_id = null;
      this.inspectionPhotoFiles = [];
      this.form = newForm;
    },
    placeholder(field) {
      if (field.attributes.placeholder) {
        return field.attributes.placeholder.capitalize();
      }
      return field.label.capitalize();
    },
  },
};
</script>

<style scoped>
.status-choice-list {
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  overflow: hidden;
}

.status-choice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 12px 14px;
  border-bottom: 1px solid #dbdbdb;
  cursor: pointer;
}

.status-choice:last-child {
  border-bottom: none;
}

.status-choice.is-selected {
  background: #eef1f7;
}

.status-choice-radio {
  margin-top: 4px;
  accent-color: #131627;
  flex-shrink: 0;
}

.status-choice-title {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #363636;
}

.status-choice.is-selected .status-choice-title {
  color: #131627;
}

.status-choice-desc {
  display: block;
  font-size: 13px;
  line-height: 1.45;
  color: #6b7280;
  margin-top: 2px;
}

.ready-module {
  margin-top: 14px;
  padding: 12px;
  background: #fafbfd;
  border: 1px dashed #d5d9e2;
  border-radius: 6px;
}

.ready-hint {
  font-size: 12.5px;
  color: #6b7280;
  margin-bottom: 8px;
}

.inspection-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.inspection-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dbdbdb;
  cursor: pointer;
}

.inspection-thumb:hover {
  outline: 2px solid #e02b2b;
}
/* Long attestation sentences: normal weight, only the key words bold */
.status-choice-title.is-sentence {
  font-weight: 400;
}
</style>
